import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import VideocamIcon from '@mui/icons-material/Videocam';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { useNavigate, useParams } from 'react-router-dom';

import { getClientDetail } from '../../services/clientService';
import { sendCameraHeartbeat } from '../../services/cameraService';
import config from '../../config/app.config';
import LivePreviewDialog from './components/monitoring/LivePreviewDialog';
import CameraList from '../../components/cameras/CameraList';

const ClientWorkspacePage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [previewCamera, setPreviewCamera] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingPreviewId, setPendingPreviewId] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const apiBase = useMemo(() => config.api.baseURL.replace(/\/$/, ''), []);

  const loadClient = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getClientDetail(clientId);
      setClient(data);
    } catch (err) {
      setError('Unable to load client workspace. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const handleBack = () => navigate('/clients');

  const handleTabChange = (_event, value) => setTab(value);

  const displayName = useMemo(() => {
    if (!client) return `Client ${clientId}`;
    const explicit = client.name;
    if (explicit) return explicit;
    const fullName = [client.first_name, client.last_name].filter(Boolean).join(' ');
    if (fullName) return fullName;
    if (client.user_detail?.full_name) return client.user_detail.full_name;
    if (client.user?.username) return client.user.username;
    return `Client ${client.id ?? clientId}`;
  }, [client, clientId]);

  const location = useMemo(
    () => client?.address || client?.location || client?.user_detail?.address || 'Location not specified',
    [client],
  );

  const fallTotal = client?.fall_events_total ?? client?.fall_events?.total ?? 0;
  const cameraCount = client?.cameras?.length ?? client?.camera_count ?? 0;
  const primaryContact = client?.primary_contact || client?.phone || client?.user_detail?.email || 'Not assigned';
  const assignedCameras = client?.cameras ?? [];

  const formatHeartbeat = useCallback((value) => {
    if (!value) return 'No heartbeat recorded';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';
    return parsed.toLocaleString();
  }, []);

  const handleOpenPreview = async (camera) => {
    if (!camera) return;
    setPreviewError('');
    setPendingPreviewId(camera.id);
    setPreviewLoading(true);
    try {
      await sendCameraHeartbeat(camera.id, { keepaliveSeconds: 120, source: 'client_workspace' });
      setPreviewCamera(camera);
    } catch (err) {
      setPreviewError('Unable to open the live preview at the moment. Please try again.');
    } finally {
      setPreviewLoading(false);
      setPendingPreviewId(null);
    }
  };

  const handleClosePreview = () => setPreviewCamera(null);

  return (
    <Container maxWidth="xl" sx={{ py: 5 }}>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Breadcrumbs>
            <Link underline="hover" color="inherit" onClick={handleBack} sx={{ cursor: 'pointer' }}>
              Clients
            </Link>
            <Typography color="text.primary">{displayName}</Typography>
          </Breadcrumbs>
          <Button
            variant="text"
            startIcon={<KeyboardArrowLeftIcon />}
            onClick={handleBack}
            sx={{ alignSelf: 'flex-start' }}
          >
            Back to directory
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <Card
              sx={{
                borderRadius: '12px !important',
                p: { xs: 3, md: 4 },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ flexWrap: 'wrap' }}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700}>
                    {displayName}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {location}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={`ID: ${client?.id ?? clientId}`}
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                    {client?.status && (
                      <Chip
                        label={client.status.replace(/_/g, ' ').toLowerCase()}
                        size="small"
                        color={client.status === 'ACTIVE' ? 'success' : 'default'}
                        sx={{ borderRadius: 1, textTransform: 'capitalize' }}
                      />
                    )}
                    {client?.care_level && (
                      <Chip
                        label={`Care level: ${client.care_level}`}
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                  </Stack>
                </Stack>

                <Stack spacing={1.5} sx={{ minWidth: { md: 260 } }}>
                  <SummaryRow
                    icon={<WarningAmberIcon color={fallTotal > 0 ? 'warning' : 'action'} />}
                    label="Falls (30 days)"
                    value={fallTotal}
                  />
                  <SummaryRow
                    icon={<VideocamIcon color="primary" />}
                    label="Assigned cameras"
                    value={cameraCount}
                  />
                  <SummaryRow
                    icon={<LocalHospitalIcon color="action" />}
                    label="Primary contact"
                    value={primaryContact}
                  />
                </Stack>
              </Stack>
            </Card>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tab} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
                <Tab icon={<TimelineIcon />} iconPosition="start" label="Incident timeline" />
                <Tab icon={<VideocamIcon />} iconPosition="start" label="Cameras" />
                <Tab icon={<WarningAmberIcon />} iconPosition="start" label="Recordings" />
              </Tabs>
            </Box>

            <Box>
              {tab === 0 && (
                <PlaceholderCard
                  title="Incident timeline"
                  description="We'll surface falls, acknowledgements, and care notes here once the timeline component is wired up."
                />
              )}
              {tab === 1 && (
                <CameraList
                  title="Assigned cameras"
                  description="Launch a live preview or review the latest heartbeat status for each camera linked to this client."
                  cameras={assignedCameras}
                  loading={loading}
                  error={previewError}
                  onRefresh={loadClient}
                  refreshLabel="Refresh list"
                  formatHeartbeat={formatHeartbeat}
                  onPreview={handleOpenPreview}
                  previewLoading={previewLoading}
                  pendingPreviewId={pendingPreviewId}
                  emptyMessage="No cameras are currently assigned. Assign a camera to this client to enable monitoring from this workspace."
                  
                />
              )}
              {tab === 2 && (
                <PlaceholderCard
                  title="Recordings"
                  description="This space will host the shared video gallery so admins can review and download clips by camera or date."
                />
              )}
            </Box>
          </>
        )}
      </Stack>

      <LivePreviewDialog
        open={Boolean(previewCamera)}
        camera={previewCamera}
        apiBase={apiBase}
        onClose={handleClosePreview}
      />
    </Container>
  );
};

const SummaryRow = ({ icon, label, value }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{
      p: 1.5,
      border: '1px solid rgba(122, 59, 193, 0.08)',
      borderRadius: 1.5,
      background: '#F9FAFF',
    }}
  >
    <Box sx={{ display: 'flex' }}>{icon}</Box>
    <Stack>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  </Stack>
);

const PlaceholderCard = ({ title, description }) => (
  <Card sx={{ p: 4, borderRadius: '12px !important' }}>
    <Typography variant="h6" fontWeight={600} gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Card>
);

export default ClientWorkspacePage;













